import { Pagination, Space } from "antd";
import { apiV2Root } from "consts";
import bem from "easy-bem";
import type {
  ActivityLogItem,
  ActivityPersonBrief,
} from "features/activity-log/data/types";
import { buildContentRoute } from "features/links/utils/shortLink";
import { useDataQueryParams } from "hooks/useDataQueryParams";
import React, { useEffect, useState } from "react";
import { PaginatedResponse } from "types/basic";

import "./ActivityFeed.css";

const cn = bem("activity-feed");

const LINKABLE_KINDS = ["problem", "solution", "card", "comment", "person"];

// Как в CommentsFeed: если у цели нет своего заголовка (у карточек title часто
// пустой), подписываем ссылку общим словом по типу + id, а не оставляем без ссылки.
const genericTargetLabels: Record<string, string> = {
  problem: "Проблема",
  solution: "Решение",
  card: "Карточка атрибута",
  comment: "Комментарий",
  person: "Профиль",
};

// Модераторские типы событий: имя модератора-actor'а в уведомлениях не
// показывается и не ссылается на профиль (анонимность модераторских действий).
const MODERATION_SLUGS = new Set([
  "moderator_deleted_content",
  "moderator_hid_content",
  "account_restricted",
  "account_restriction_lifted",
]);

const personName = (person: ActivityPersonBrief | null): string | null => {
  if (!person) return null;
  const name = [person.firstName, person.secondName].filter(Boolean).join(" ");

  return name || (person.userId ? `Пользователь ${person.userId}` : null);
};

const personLink = (person: ActivityPersonBrief | null): string | null => {
  if (!person || !person.id) return null;

  const route = buildContentRoute({ kind: "person", id: person.id });

  return `${window.location.origin}/#${route.pathname}${route.hash}`;
};

const getTargetLabel = (item: ActivityLogItem): string | null => {
  const target = item.target;

  if (!target) return null;
  if (target.title) return target.title;

  const generic = genericTargetLabels[target.kind];

  return generic ? `${generic} #${target.id}` : `Объект #${target.id}`;
};

// Веса приходят только модераторам (см. ActivityFeedView.list, include_weights) —
// если их нет в ответе, обычный пользователь эту строку никогда не увидит.
const getWeightsLabel = (item: ActivityLogItem): string | null => {
  const { engagementWeight, authorityWeight, learningWeight } =
    item.activityType;

  if (
    engagementWeight === undefined ||
    authorityWeight === undefined ||
    learningWeight === undefined
  ) {
    return null;
  }

  const parts = (
    [
      ["E", engagementWeight],
      ["A", authorityWeight],
      ["L", learningWeight],
    ] as Array<[string, number]>
  )
    .filter(([, value]) => value !== 0)
    .map(([label, value]) => `${label}:${value > 0 ? "+" : ""}${value}`);

  return parts.length > 0 ? parts.join(" ") : "не даёт очков";
};

const getTargetLink = (item: ActivityLogItem): string | null => {
  const target = item.target;

  if (!target || !LINKABLE_KINDS.includes(target.kind)) return null;

  const route = buildContentRoute({
    kind: target.kind as "problem" | "solution" | "card" | "comment" | "person",
    id: target.id,
    problemId: target.problemId ?? undefined,
    solutionId: target.solutionId ?? undefined,
    cardId: target.cardId ?? undefined,
  });

  return `${window.location.origin}/#${route.pathname}${route.hash}`;
};

type ActivityFeedProps = {
  personId?: number;
  // 'own' (по умолчанию) — своя/чужая активность (actor), см. блок выше.
  // 'notifications' — то, что должно уведомлять текущего пользователя
  // (target_owner/aux_user с notify-флагом); всегда только для себя,
  // personId в этом режиме бэкенд игнорирует.
  feed?: "own" | "notifications";
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  personId,
  feed = "own",
}) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [personId, feed]);

  const feedResponse = useDataQueryParams<PaginatedResponse<ActivityLogItem>>(
    "activity-log",
    { person_id: feed === "own" ? personId : undefined, page, feed },
    apiV2Root,
  );

  if (!feedResponse) {
    return <div>Загрузка журнала активности...</div>;
  }

  const items = feedResponse.results || [];

  return (
    <div className={cn()}>
      {items.length === 0 ? (
        <div>Пока никаких действий не зафиксировано.</div>
      ) : (
        <Space
          direction="vertical"
          size="small"
          style={{ width: "100%" }}
        >
          {items.map((item) => {
            const targetLink = getTargetLink(item);
            const targetLabel = getTargetLabel(item);
            const auxUserName = personName(item.auxUser);
            // Веса — черновой инструмент модератора для журнала своей/чужой
            // активности; в уведомлениях они посторонний шум ("сколько очков
            // тебе дало чужое действие" никого не должно волновать здесь).
            const weightsLabel =
              feed === "notifications" ? null : getWeightsLabel(item);
            // Кто это сделал — интересно только в уведомлениях (feed=own —
            // это всегда сам зритель, показывать его имя незачем).
            const isModerationEntry = MODERATION_SLUGS.has(
              item.activityType.slug,
            );
            const actorName =
              feed === "notifications" ? personName(item.actor) : null;
            const actorLink =
              actorName && !isModerationEntry ? personLink(item.actor) : null;

            return (
              <div
                key={item.id}
                className={cn("item")}
              >
                <div className={cn("item-main")}>
                  {actorName && (
                    <span className={cn("item-actor")}>
                      {isModerationEntry ? (
                        "Модератор"
                      ) : actorLink ? (
                        <a href={actorLink}>{actorName}</a>
                      ) : (
                        actorName
                      )}
                      {" — "}
                    </span>
                  )}
                  <span className={cn("item-title")}>
                    {item.activityType.title}
                  </span>
                  {targetLabel && (
                    <>
                      {" — "}
                      {targetLink ? (
                        <a href={targetLink}>{targetLabel}</a>
                      ) : (
                        targetLabel
                      )}
                    </>
                  )}
                  {auxUserName && (
                    <span className={cn("item-aux")}>
                      {" → "}
                      {auxUserName}
                    </span>
                  )}
                </div>
                {item.comment && (
                  <div className={cn("item-comment")}>{item.comment}</div>
                )}
                {weightsLabel && (
                  <div className={cn("item-weights")}>{weightsLabel}</div>
                )}
                <div className={cn("item-date")}>
                  {new Date(item.createdAt).toLocaleString("ru-RU")}
                </div>
              </div>
            );
          })}
        </Space>
      )}

      {feedResponse.totalPages > 1 && (
        <div className={cn("pagination")}>
          <Pagination
            current={feedResponse.page}
            pageSize={feedResponse.pageSize}
            total={feedResponse.count}
            onChange={setPage}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
